import React, { useEffect, useState } from 'react';

import api from '../../../../services/api';

import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { Controller, useForm } from 'react-hook-form';
import { ArrowLeft, Book, Image, Trash } from 'phosphor-react';
import toast from 'react-hot-toast';

import LoaderBIO from '../../../../components/Loader';
import Input from '../../../../components/Form/Input';
import SimpleMultiSelect from '../../../../components/Form/SimpleMultiSelect';
import MessageErrorValidator from '../../../../components/MessageErrorValidator';

import {
  BtnRemove,
  Container,
  HeaderSection,
  Line1,
  Line2,
  Line4,
  LineButton,
  Poster,
  SectionContainer1,
  Separator,
} from './styles';
import { IGenericOptions } from '../../../../interfaces/IGenericOptions';
import Button from '../../../../components/Button';
import Textarea from '../../../../components/Form/Textarea';
import DropZone from '../../../../components/Form/DropZone';

import { loadConfettiAllPage } from '../../../../utils/useConfetti';
import { IMaterialProps } from '../../../../interfaces/Material';
import { FakeDropZone } from '../../AulaEdit/styles';

interface IProps {
  onClose(): void;
  onMaterialSaved(values: any): void;
  editingMaterial?: IMaterialProps;
}

const validationSchema = Joi.object({
  name: Joi.string().min(10).max(100).required().messages({
    '*': 'Informe um título válido.',
    'string.min': `O campo título deve ter no mínimo {#limit} caracteres`,
    'string.max': `O campo título deve ter no máximo {#limit} caracteres`,
  }),
  description: Joi.string().min(20).max(350).messages({
    '*': 'Informe uma descrição válida.',
    'string.min': `O campo descrição deve ter no mínimo {#limit} caracteres`,
    'string.max': `O campo descrição deve ter no máximo {#limit} caracteres`,
  }),
  category_name: Joi.string()
    .required()
    .messages({ '*': 'Informe uma categoria válida.' }),
});

const TOAST_OPTIONS = {
  position: 'bottom-right' as const,
  duration: 5000,
  className: 'toast-samuquinha',
};

function mapCategoriesToOptions(
  categories: { value: string; label: string }[],
): IGenericOptions[] {
  return categories.map(category => ({
    value: category.label,
    label: category.label,
  }));
}

function buildMaterialFormData(
  data: any,
  materialFile: File | undefined,
  thumbnailFile: File | undefined,
): FormData {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('description', data.description);
  formData.append('category_name', data.category_name);

  if (materialFile) formData.append('file', materialFile);
  if (thumbnailFile) formData.append('thumbnail', thumbnailFile);

  return formData;
}

const CreateOrEdit: React.FC<IProps> = ({
  onClose,
  onMaterialSaved,
  editingMaterial,
}) => {
  const [materialFileList, setMaterialFileList] = useState<File[]>([]);
  const [thumbnailFileList, setThumbnailFileList] = useState<File[]>([]);
  const [categories, setCategories] = useState<IGenericOptions[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: (data, context, options) => {
      const { thumbnail: _thumbnail, ...dataWithoutThumbnail } = data;
      return joiResolver(validationSchema)(
        dataWithoutThumbnail,
        context,
        options,
      );
    },
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (editingMaterial?.id) {
      reset({
        name: editingMaterial.name,
        description: editingMaterial.description,
        category_name: editingMaterial.category.name,
      });
    }
  }, [editingMaterial, reset]);

  async function loadCategories() {
    try {
      setIsLoadingCategories(true);
      const response = await api.get('/materials/categories/options');
      setCategories(mapCategoriesToOptions(response.data));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCategories(false);
    }
  }

  function validateMaterialFilePresence(): boolean {
    if (materialFileList.length === 0 && !editingMaterial) {
      toast.error(
        'É necessário inserir um arquivo para o material.',
        TOAST_OPTIONS,
      );
      return false;
    }
    return true;
  }

  async function saveMaterial(formData: FormData): Promise<IMaterialProps> {
    if (editingMaterial?.id) {
      const response = await api.put(
        `/materials/${editingMaterial.id}`,
        formData,
      );
      return response.data;
    }

    const response = await api.post('/materials', formData);
    return response.data;
  }

  function handleSaveSuccess(savedMaterial: IMaterialProps) {
    const isEditing = !!editingMaterial?.id;
    const message = isEditing
      ? 'Material editado com sucesso!'
      : 'Material cadastrado com sucesso!';

    toast.success(message, TOAST_OPTIONS);
    onClose();

    if (isEditing) {
      onMaterialSaved((previous: IMaterialProps[]) =>
        previous.map(material =>
          material.id === savedMaterial.id ? savedMaterial : material,
        ),
      );
    } else {
      onMaterialSaved((previous: IMaterialProps[]) => [savedMaterial, ...previous]);
    }

    loadConfettiAllPage();
  }

  function handleRemoveThumbnail() {
    if (
      !window.confirm('Deseja realmente remover a imagem de capa do material?')
    )
      return;

    setThumbnailFileList([]);
    setValue('thumbnail', '');
    editingMaterial!.thumbnail = '';
    editingMaterial!.thumbnail_url = '';
  }

  const onSubmit = handleSubmit(async data => {
    if (!validateMaterialFilePresence()) return;

    try {
      setIsSubmitting(true);

      const formData = buildMaterialFormData(
        data,
        materialFileList[0],
        thumbnailFileList[0],
      );
      const savedMaterial = await saveMaterial(formData);

      handleSaveSuccess(savedMaterial);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  });

  const shouldShowThumbnailUpload =
    !editingMaterial || !editingMaterial.thumbnail_url;
  const hasExistingThumbnail = !!editingMaterial?.thumbnail_url;

  return (
    <>
      <LoaderBIO isVisible={isLoadingCategories} />

      <Container>
        <HeaderSection>
          <div onClick={() => onClose()}>
            <ArrowLeft weight="duotone" /> Voltar para a listagem de materiais
          </div>
        </HeaderSection>

        <SectionContainer1>
          <form onSubmit={onSubmit}>
            <Separator>
              <Book size={20} weight="duotone" />
              <h2>Informe os dados do Material abaixo:</h2>
            </Separator>

            <Line1>
              <div>
                <Controller
                  name="category_name"
                  control={control}
                  render={({ field }) => (
                    <SimpleMultiSelect
                      register={register}
                      name={field.name}
                      label="Categoria do material"
                      placeHolder="Selecione..."
                      options={categories}
                      setResult={values => setValue('category_name', values)}
                      defaultValue={field.value}
                      isMulti={false}
                      required
                    />
                  )}
                />
                <MessageErrorValidator>
                  {errors?.category_name?.message as string}
                </MessageErrorValidator>
              </div>
              <div>
                <Input
                  register={register}
                  name="name"
                  type="text"
                  label="Nome do material"
                  autoComplete="off"
                />
                <MessageErrorValidator>
                  {errors?.name?.message as string}
                </MessageErrorValidator>
              </div>
            </Line1>

            <Line2>
              <div>
                <Textarea
                  register={register}
                  name="description"
                  type="text"
                  label="Descrição do material"
                  autoComplete="off"
                />
                <MessageErrorValidator>
                  {errors?.description?.message as string}
                </MessageErrorValidator>
              </div>
            </Line2>

            <Separator>
              <Book size={20} weight="bold" />
              <h2>Insira o arquivo do material (pdf ou doc):</h2>
            </Separator>
            <Line4>
              <div>
                <DropZone
                  setFilesData={setMaterialFileList}
                  maxSize={1024 * 1024 * 5}
                  isMulti={false}
                  icons={false}
                  accept={{
                    'application/pdf': ['.pdf'],
                    'application/msword': ['.doc', '.docx'],
                  }}
                />
              </div>
            </Line4>

            {shouldShowThumbnailUpload && (
              <>
                <Separator>
                  <Image size={20} weight="bold" />
                  <h2>Insira a imagem de capa do material</h2>
                </Separator>
                <Line4>
                  <div>
                    <DropZone
                      setFilesData={setThumbnailFileList}
                      maxSize={(1024 * 1024) / 2}
                      isMulti={false}
                      icons={false}
                      placeHolder="Insira a imagem de capa do material (Tamanho recomendado 225x150px)"
                      accept={{
                        'image/png': ['.png', '.jpg', '.jpeg', '.webp'],
                      }}
                    />
                  </div>
                </Line4>
              </>
            )}

            {hasExistingThumbnail && (
              <>
                <Separator style={{ marginTop: '1rem' }}>
                  <Book size={20} weight="bold" />
                  <h2>Imagem destaque do material:</h2>
                </Separator>
                <div>
                  <FakeDropZone>
                    <Poster>
                      <BtnRemove onClick={handleRemoveThumbnail}>
                        <Trash size={20} weight="duotone" />
                      </BtnRemove>
                      <img src={editingMaterial!.thumbnail_url} alt="" />
                    </Poster>
                  </FakeDropZone>
                </div>
              </>
            )}

            <LineButton>
              <Button type="submit" loading={isSubmitting} color="primary">
                {editingMaterial ? 'Atualizar Material' : 'Cadastrar Material'}
              </Button>
            </LineButton>
          </form>
        </SectionContainer1>
      </Container>
    </>
  );
};

export default CreateOrEdit;
